// @flow
import React, { useState, useEffect } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { compose } from 'ramda';
import ScrollLock from 'react-scrolllock';
import { Scrollbars } from 'react-custom-scrollbars';
import * as querystring from 'query-string';
import { connect } from 'react-redux';
import OutsideClickHandler from 'react-outside-click-handler';
import { updateUI as updateUIAction } from '../../stores/ui';
import LegacyLink from '../Common/LegacyLink';
import Logo from '../UI/Logo';
import LogoSmall from '../../../static/images/header_logo_small.svg';
import withAnalytics from '../Decorators/withAnalytics';
import PrimaryNav from './HeaderPartials/PrimaryNav';
import SecondaryNav from './HeaderPartials/SecondaryNav';
import TertiaryNav from './HeaderPartials/TertiaryNav';
import FindParkingCTA from './HeaderPartials/FindParkingCTA';
import Brand from '../UI/Brand';
import SuperUserBar from './SuperUserBar';
import SearchResultsFilterButton from '../Search/SearchResultsFilters/SearchResultsFilterButton';

import '../../../static/scss/components/header.scss';

type MainNavigationProps = {
  showSearchLink: boolean,
  isStatic: boolean,
  secondaryClass: string,
  isAdmin: boolean,
  user: ?any,
  theme: any,
  secondaryVisible: boolean,
  toggleSecondary: () => void
};

const MainNavigation = ({
  showSearchLink,
  isStatic,
  secondaryClass,
  isAdmin,
  user,
  theme,
  secondaryVisible,
  toggleSecondary
}: MainNavigationProps) => {
  const {
    hideCPM,
    hideFindParking,
    hidePrimaryNav,
    checkout,
    searchResults
  } = theme;
  const renderPrimaryLinks =
    hidePrimaryNav || Boolean(checkout) || Boolean(searchResults);

  return (
    <>
      <PrimaryNav
        showSearchLink={showSearchLink}
        isStatic={isStatic}
        renderPrimaryLinks={renderPrimaryLinks}
        hideFindParking={hideFindParking}
        hideCPM={hideCPM}
        openHelpInNewTab={Boolean(checkout)}
      />
      <OutsideClickHandler
        onOutsideClick={toggleSecondary}
        disabled={!secondaryVisible}
      >
        <SecondaryNav
          secondaryClass={secondaryClass}
          isAdmin={isAdmin}
          user={user}
          isStatic={isStatic}
          hideFindParking={hideFindParking}
          secondaryVisible={secondaryVisible}
          toggleSecondary={toggleSecondary}
        />
      </OutsideClickHandler>
    </>
  );
};

const conditionallyRenderTertiaryNav = location => {
  const tertiaryDisabled = ['rent-your-space'];

  if (location && location.search) {
    const { showTertiaryNavigation, page } = querystring.parse(location.search);
    if (
      !!parseInt(showTertiaryNavigation, 10) &&
      !tertiaryDisabled.includes(page)
    ) {
      return (
        <div className="c-header__row">
          <TertiaryNav location={location} />
        </div>
      );
    }
  }
  return null;
};

type HeaderProps = {
  user?: any,
  ui?: any,
  location?: any,
  isStatic?: boolean,
  theme?: any,
  mobileHeader?: boolean,
  analytics: any,
  updateUI?: any
};

const headerClass = 'c-header';

export const Header = ({
  user = undefined,
  location = undefined,
  isStatic = false,
  updateUI = () => {},
  analytics,
  ui: uiState = {},
  theme = {},
  mobileHeader = false
}: HeaderProps) => {
  const [mobileVisible, setMobileVisible] = useState(false);
  const [secondaryVisible, setSecondaryVisible] = useState(false);

  const pathname = location?.pathname;

  useEffect(
    () => {
      if (pathname) {
        setMobileVisible(false);
      }
    },
    [pathname]
  );

  const toggleMobileMenu = () => {
    setMobileVisible(!mobileVisible);
  };

  const toggleSecondary = e => {
    if (window && window.innerWidth >= 860) {
      e.preventDefault();

      setSecondaryVisible(!secondaryVisible);
    }
  };

  const isAdmin = !!(user && user.isAdmin);
  const menuContainerClass = mobileVisible
    ? `${headerClass}__menu-container--visible`
    : '';
  const secondaryClass = `${headerClass}__menu${
    secondaryVisible ? ` ${headerClass}__menu--visible` : ''
  }`;
  const userMenuClass = user ? '' : ' c-header__menu--user-no-auth';

  const { checkout, searchResults, minimalBackground, darkMobileMenu } = theme;
  const checkoutClass = checkout ? '--checkout' : '';
  const searchResultsClass = searchResults ? '--searchResults' : '';
  const headerBackground = searchResults && !mobileHeader ? 'dark' : 'light';
  const darkMobileMenuClass = darkMobileMenu
    ? `${headerClass}--darkMobileMenu`
    : '';

  const tertiaryNavigation = conditionallyRenderTertiaryNav(location);
  const showSearchLink = !!tertiaryNavigation;

  return (
    <header
      className={`${headerClass} ${
        minimalBackground ? `${headerClass}--background-minimal` : ''
      } ${
        tertiaryNavigation ? `${headerClass}--has-tertiary` : ''
      } ${headerClass}${checkoutClass} ${headerClass}${searchResultsClass} ${darkMobileMenuClass}`}
    >
      <div className="c-header__row">
        <div className={`c-header__logo c-header__logo${checkoutClass}`}>
          <span className="c-header__logo-container">
            <Logo theme={headerBackground} />
          </span>
          {checkout && (
            <div className="c-header__logo--secure">
              <p>Secure Payment</p>
            </div>
          )}
        </div>
        <Brand wrap color>
          <div
            className={`c-header__mobile-anchor c-header__mobile-anchor${checkoutClass}`}
            onClick={toggleMobileMenu}
          />
        </Brand>
        {!!searchResults &&
          mobileHeader && (
            <SearchResultsFilterButton searchQuery={location.search} />
          )}
        <FindParkingCTA
          updateUI={updateUI}
          analytics={analytics}
          headerShouldShowCTA={uiState.headerShouldShowCTA}
        />
        <div className={`c-header__menu-container ${menuContainerClass}`}>
          {mobileVisible && (
            <div className="c-header__logo-small">
              <img src={LogoSmall} alt="JustPark" />
            </div>
          )}
          <ScrollLock isActive={mobileVisible}>
            <div className="c-header__mobile-scroll">
              {__CLIENT__ && window.innerWidth < 780 ? (
                <Scrollbars autoHeight autoHeightMin={500}>
                  <MainNavigation
                    showSearchLink={showSearchLink}
                    isStatic={isStatic}
                    secondaryClass={secondaryClass}
                    isAdmin={isAdmin}
                    user={user}
                    theme={theme}
                    secondaryVisible={secondaryVisible}
                    toggleSecondary={toggleSecondary}
                  />
                </Scrollbars>
              ) : (
                <MainNavigation
                  showSearchLink={showSearchLink}
                  isStatic={isStatic}
                  secondaryClass={secondaryClass}
                  isAdmin={isAdmin}
                  user={user}
                  theme={theme}
                  secondaryVisible={secondaryVisible}
                  toggleSecondary={toggleSecondary}
                />
              )}
            </div>
          </ScrollLock>
          <ul className={`c-header__menu c-header__menu--user${userMenuClass}`}>
            <li>
              {user && (
                <Brand color>
                  <LegacyLink to="dashboard/" onClick={toggleSecondary}>
                    {user.unreadMessages > 0 && (
                      <div className="c-header__menu--message-count">
                        {user.unreadMessages}
                      </div>
                    )}
                    {`${user.firstName} ${user.lastName &&
                      user.lastName.charAt(0).toUpperCase()}`}

                    <Brand>
                      <div
                        className={`c-header__menu--user__dropdown c-header__menu--user__dropdown${checkoutClass}`}
                      />
                    </Brand>
                  </LegacyLink>
                </Brand>
              )}
              {!user && (
                <Brand color>
                  <Link
                    onClick={() =>
                      analytics.track('click:nav:login', {
                        page: window.location.pathname.toLowerCase()
                      })
                    }
                    to="/auth/login"
                    className="c-header__menu--login-link"
                  >
                    Login
                  </Link>
                </Brand>
              )}
            </li>
            {!user && [
              <li key="registration">
                <Brand color borderColor>
                  <Link
                    to="/registration"
                    className="c-header__menu--login-link"
                  >
                    Sign Up
                  </Link>
                </Brand>
              </li>
            ]}
          </ul>
        </div>
        {mobileVisible && (
          <div
            className="c-header__menu-overlay c-header__menu-overlay--visible"
            onClick={toggleMobileMenu}
          />
        )}
      </div>
      {tertiaryNavigation}
      <SuperUserBar user={user} />
    </header>
  );
};

const mapStateToProps = state => ({
  ui: state.ui,
  mobileHeader: state.browser.lessThan.medium
});

const mapDispatchToProps = {
  updateUI: updateUIAction
};

export default compose(
  withRouter,
  withAnalytics({}, {}),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(Header);
